import { useMemo, useState } from "react";
import { ArrowRight, GitBranch, Wand2 } from "lucide-react";
import { JsonPanel } from "../playground/JsonPanel";
import { evaluate, showValue } from "../playground/eval";

/* Workflows PLAYGROUND — the dead-simple, read-only twin of the component
 * playground. No free editing, no BeXO: we show ONE fixed sample workflow
 * ("place_order") as a vertical flow, let the user pick from a few predefined
 * orders, and run every step live in the browser with the real expression
 * evaluator. Each step is laid out the same way: what it reads → what it
 * produced → its JSON. Flat interfaces only (narrowing of discriminated unions
 * is unreliable in this tsconfig). ES5-safe (no regex /y, no matchAll). */

// ---------------------------------------------------------------------------
// The fixed sample workflow (read-only). Flat shape — `kind` selects fields.
// ---------------------------------------------------------------------------

interface WfStep {
  id: string;
  kind: "transform" | "branch";
  title: string; // friendly heading
  explain: string; // plain-language description
  exprShown: string; // the expression as shown to the reader
  reads: string[]; // paths this step consumes (for the input box)
  value: string; // transform: the expression to evaluate
  when: string; // branch: the condition
  then: string; // branch: value when true
  els: string; // branch: value when false
}

const STEPS: WfStep[] = [
  {
    id: "total",
    kind: "transform",
    title: "Work out the order total",
    explain: "Multiply the price by how many items the customer wants.",
    exprShown: "$.input.price * $.input.quantity",
    reads: ["$.input.price", "$.input.quantity"],
    value: "$.input.price * $.input.quantity",
    when: "",
    then: "",
    els: "",
  },
  {
    id: "tier",
    kind: "branch",
    title: "Decide the order tier",
    explain: "More than 2 items is a bulk order; anything else is standard.",
    exprShown: "$.input.quantity > 2 ? 'bulk' : 'standard'",
    reads: ["$.input.quantity"],
    value: "",
    when: "$.input.quantity > 2",
    then: "'bulk'",
    els: "'standard'",
  },
  {
    id: "discount",
    kind: "transform",
    title: "Apply a bulk discount",
    explain: "Bulk orders get 10% off the total (rounded to 2 decimals); standard orders get nothing.",
    exprShown: "if(tier == 'bulk', round(total * 0.1, 2), 0)",
    reads: ["$.steps.tier.output", "$.steps.total.output"],
    value: "if($.steps.tier.output == 'bulk', round($.steps.total.output * 0.1, 2), 0)",
    when: "",
    then: "",
    els: "",
  },
  {
    id: "summary",
    kind: "transform",
    title: "Write a friendly summary",
    explain: "Combine the customer's name with what they actually pay (total minus discount).",
    exprShown: "concat(customer, ' pays $', total - discount)",
    reads: ["$.input.customer", "$.steps.total.output", "$.steps.discount.output"],
    value: "concat($.input.customer, ' pays $', to_text($.steps.total.output - $.steps.discount.output))",
    when: "",
    then: "",
    els: "",
  },
];

// ---------------------------------------------------------------------------
// Predefined inputs (the only thing the user changes).
// ---------------------------------------------------------------------------

interface OrderCase {
  id: string;
  label: string;
  quantity: number;
  price: number;
  customer: string;
}

const CASES: OrderCase[] = [
  { id: "single", label: "1 × $50", quantity: 1, price: 50, customer: "Sam" },
  { id: "bulk", label: "5 × $20", quantity: 5, price: 20, customer: "Ada" },
  { id: "edge", label: "3 × $30", quantity: 3, price: 30, customer: "Lee" },
];

// ---------------------------------------------------------------------------
// Predefined expressions for the little "Expressions" subsection.
// ---------------------------------------------------------------------------

interface ExprDemo {
  fn: string;
  src: string;
  desc: string;
}

const EXPR_DEMOS: ExprDemo[] = [
  { fn: "concat", src: "concat($.input.customer, ' buys ', to_text($.input.quantity))", desc: "Glue values into one string." },
  { fn: "if", src: "if($.input.quantity > 2, 'bulk', 'standard')", desc: "Choose one of two values." },
  { fn: "round", src: "round($.input.price * $.input.quantity * 0.1, 2)", desc: "Round a number to 2 decimals." },
  { fn: "sum", src: "sum(split('10,20,30', ','))", desc: "Add up a list of numbers." },
];

// ---------------------------------------------------------------------------
// Client-side runner — uses the real evaluator. Flat result rows.
// ---------------------------------------------------------------------------

// Mirror the evaluator's truthiness (its `truthy` is not exported).
function truthyish(v: unknown): boolean {
  if (v == null || v === false || v === 0 || v === "") return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === "object") return Object.keys(v as object).length > 0;
  return Boolean(v);
}

interface StepResult {
  id: string;
  kind: string;
  ok: boolean;
  outDisplay: string;
  error: string;
  took: string; // "" | "then" | "else"
  json: unknown; // the per-step JSON document
}

interface RunState {
  results: StepResult[];
  ctx: Record<string, unknown>; // { input, steps }
  finalOk: boolean;
  finalDisplay: string;
}

function runWorkflow(input: Record<string, unknown>): RunState {
  const steps: Record<string, unknown> = {};
  const ctx: Record<string, unknown> = { input: input, steps: steps };
  const results: StepResult[] = [];

  for (let i = 0; i < STEPS.length; i++) {
    const s = STEPS[i]!;
    let ok = true;
    let error = "";
    let output: unknown = null;
    let took = "";

    if (s.kind === "transform") {
      const r = evaluate(s.value, ctx);
      if (r.ok) output = r.value;
      else { ok = false; error = r.error ?? "error"; }
    } else {
      const w = evaluate(s.when, ctx);
      if (!w.ok) { ok = false; error = w.error ?? "error"; }
      else {
        took = truthyish(w.value) ? "then" : "else";
        const side = took === "then" ? s.then : s.els;
        const r = evaluate(side, ctx);
        if (r.ok) output = r.value;
        else { ok = false; error = r.error ?? "error"; }
      }
    }

    steps[s.id] = { output: ok ? output : null };

    const json: Record<string, unknown> =
      s.kind === "transform"
        ? { id: s.id, transform: { value: s.value }, output: ok ? output : null }
        : { id: s.id, branch: { when: s.when, then: s.then, else: s.els }, took: took, output: ok ? output : null };

    results.push({
      id: s.id,
      kind: s.kind,
      ok: ok,
      outDisplay: ok ? showValue(output) : "",
      error: error,
      took: took,
      json: json,
    });
  }

  const last = results.length > 0 ? results[results.length - 1]! : undefined;
  return {
    results: results,
    ctx: ctx,
    finalOk: last ? last.ok : false,
    finalDisplay: last ? last.outDisplay : "",
  };
}

// Friendly short label for a path: $.steps.total.output -> total ; $.input.price -> price
function labelFor(path: string): string {
  const body = path.replace(/^\$\./, "");
  const parts = body.split(".");
  if (parts[0] === "steps" && parts.length >= 2) return parts[1] ?? path;
  return parts.length > 0 ? parts[parts.length - 1] ?? path : path;
}

// ---------------------------------------------------------------------------
// Shared inline styles (everything else uses guide.css classes).
// ---------------------------------------------------------------------------

const mono: React.CSSProperties = { fontFamily: "var(--gd-mono)" };

const ioLabel: React.CSSProperties = {
  fontSize: 10.5,
  fontFamily: "var(--gd-mono)",
  color: "var(--gd-muted)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  marginBottom: 6,
};

const exprBox: React.CSSProperties = {
  marginTop: 10,
  fontFamily: "var(--gd-mono)",
  fontSize: 12.5,
  color: "var(--gd-text)",
  background: "var(--gd-bg)",
  border: "1px solid var(--gd-border)",
  borderRadius: "var(--gd-radius-sm)",
  padding: "8px 10px",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const kindBadge: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "2px 8px",
  borderRadius: 6,
  fontSize: 11,
  fontFamily: "var(--gd-mono)",
  background: "color-mix(in srgb, var(--gd-primary) 12%, var(--gd-surface))",
  color: "var(--gd-primary)",
  border: "1px solid color-mix(in srgb, var(--gd-primary) 28%, transparent)",
};

const numBadge: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 22,
  height: 22,
  borderRadius: "50%",
  background: "color-mix(in srgb, var(--gd-primary) 14%, var(--gd-surface))",
  color: "var(--gd-primary)",
  border: "1px solid color-mix(in srgb, var(--gd-primary) 30%, transparent)",
  fontFamily: "var(--gd-mono)",
  fontSize: 11.5,
  fontWeight: 700,
  flex: "none",
};

// ---------------------------------------------------------------------------
// Component.
// ---------------------------------------------------------------------------

export function WorkflowsPlayground() {
  const [caseId, setCaseId] = useState<string>(CASES[0]!.id);

  const activeCase = CASES.find((c) => c.id === caseId) ?? CASES[0]!;
  const input: Record<string, unknown> = useMemo(
    () => ({ quantity: activeCase.quantity, price: activeCase.price, customer: activeCase.customer }),
    [activeCase]
  );

  // Re-runs automatically whenever the chosen order changes.
  const run = useMemo(() => runWorkflow(input), [input]);

  const resultFor = (id: string): StepResult | undefined => run.results.find((r) => r.id === id);

  // Resolve a step's read-paths against the live run context, for the input box.
  function readsView(paths: string[]): { label: string; value: string }[] {
    return paths.map((p) => {
      const r = evaluate(p, run.ctx);
      return { label: labelFor(p), value: r.ok ? showValue(r.value) : "—" };
    });
  }

  return (
    <div className="gd-section">
      <div className="gd-section-intro">
        <h2>Workflows — playground</h2>
        <p>
          A <strong>workflow</strong> is the automation behind your screens: a list of named, ordered{" "}
          <strong>steps</strong> that shape data. Each step saves its result under{" "}
          <code>$.steps.&lt;id&gt;.output</code>, so the next step can build on it. Below is one real
          sample workflow — pick an order and watch every step run.
        </p>
      </div>

      {/* ===================== PICK AN INPUT ===================== */}
      <h3 className="gd-h3">1 · Pick an order</h3>
      <p className="gd-section-intro" style={{ marginTop: 0 }}>
        This is the <code>$.input</code> the workflow receives. Choose one — everything below re-runs instantly.
      </p>
      <div className="gd-seg" style={{ flexWrap: "wrap" }}>
        {CASES.map((c) => (
          <button
            key={c.id}
            type="button"
            className={"gd-seg-btn" + (c.id === caseId ? " active" : "")}
            onClick={() => setCaseId(c.id)}
          >
            {c.label}
          </button>
        ))}
      </div>
      <div style={{ ...exprBox, marginTop: 12 }}>{JSON.stringify(input, null, 2)}</div>

      {/* ===================== THE FLOW ===================== */}
      <h3 className="gd-h3">2 · The workflow runs, step by step</h3>
      <p className="gd-section-intro" style={{ marginTop: 0 }}>
        These steps are fixed — read them top to bottom. Each one shows what it{" "}
        <strong>reads</strong>, what it <strong>produced</strong>, and its JSON.
      </p>

      <div className="gd-wf">
        {STEPS.map((step, i) => {
          const row = resultFor(step.id);
          const reads = readsView(step.reads);
          const KindIcon = step.kind === "branch" ? GitBranch : Wand2;
          return (
            <div key={step.id}>
              {i > 0 ? <div className="gd-wf-conn" /> : null}
              <div className="gd-wf-step">
                {/* header */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={numBadge}>{i + 1}</span>
                  <span style={kindBadge}>
                    <KindIcon size={12} /> {step.kind}
                  </span>
                  <strong style={{ fontSize: 14 }}>{step.title}</strong>
                  <code style={{ fontSize: 11, color: "var(--gd-muted)", marginLeft: "auto", ...mono }}>
                    id: {step.id}
                  </code>
                </div>

                <p style={{ margin: "10px 0 0", color: "var(--gd-muted)", fontSize: 13 }}>{step.explain}</p>

                {/* the expression */}
                <div style={{ ...ioLabel, marginTop: 12, marginBottom: 0 }}>expression</div>
                <div style={exprBox}>{step.exprShown}</div>
                {step.kind === "branch" && row && row.took ? (
                  <div style={{ marginTop: 8, fontSize: 12, color: "var(--gd-muted)" }}>
                    Took the <span className="gd-hl">{row.took}</span> path.
                  </div>
                ) : null}

                {/* input -> output */}
                <div className="gd-wf-io">
                  <div className="gd-wf-box">
                    <div style={ioLabel}>reads</div>
                    {reads.length === 0 ? (
                      <span style={{ color: "var(--gd-muted)" }}>nothing</span>
                    ) : (
                      reads.map((r) => (
                        <div key={r.label}>
                          <span style={{ color: "var(--gd-muted)" }}>{r.label}</span> = {r.value}
                        </div>
                      ))
                    )}
                  </div>
                  <div className="gd-wf-arrow">
                    <ArrowRight size={18} />
                  </div>
                  <div className="gd-wf-box">
                    <div style={ioLabel}>output</div>
                    {row ? (
                      row.ok ? (
                        <span className="gd-hl">{row.outDisplay}</span>
                      ) : (
                        <span style={{ color: "var(--aps-danger, #dc2626)" }}>✕ {row.error}</span>
                      )
                    ) : (
                      <span style={{ color: "var(--gd-muted)" }}>—</span>
                    )}
                  </div>
                </div>

                {/* the step's JSON */}
                {row ? (
                  <div style={{ marginTop: 12 }}>
                    <JsonPanel value={row.json} title="Step JSON" />
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {/* final output */}
      <div style={{ marginTop: 16 }}>
        {run.finalOk ? (
          <div className="gd-expr-result">
            <span className="gd-expr-arrow">★</span>
            <span>
              Workflow output (<code>$.steps.summary.output</code>): <span className="gd-hl">{run.finalDisplay}</span>
            </span>
          </div>
        ) : (
          <div className="gd-expr-result err">
            <span className="gd-expr-arrow">✕</span>
            <span>The workflow could not finish — see the failing step above.</span>
          </div>
        )}
      </div>

      <p style={{ marginTop: 12, fontSize: 12.5, color: "var(--gd-muted)", fontStyle: "italic" }}>
        This runs entirely in your browser as a faithful preview — the real engine runs these exact same steps.
      </p>

      {/* ===================== EXPRESSIONS ===================== */}
      <h3 className="gd-h3">3 · Expressions, live</h3>
      <p className="gd-section-intro" style={{ marginTop: 0 }}>
        Steps are powered by small expressions. Here are a few, evaluated right now against the order you picked.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {EXPR_DEMOS.map((d) => {
          const r = evaluate(d.src, run.ctx);
          return (
            <div key={d.fn} className="gd-wf-step">
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={kindBadge}>{d.fn}</span>
                <span style={{ fontSize: 12.5, color: "var(--gd-muted)" }}>{d.desc}</span>
              </div>
              <div className="gd-wf-io">
                <div className="gd-wf-box">
                  <div style={ioLabel}>expression</div>
                  {d.src}
                </div>
                <div className="gd-wf-arrow">
                  <ArrowRight size={18} />
                </div>
                <div className="gd-wf-box">
                  <div style={ioLabel}>result</div>
                  {r.ok ? (
                    <span className="gd-hl">{showValue(r.value)}</span>
                  ) : (
                    <span style={{ color: "var(--aps-danger, #dc2626)" }}>✕ {r.error}</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
